import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Alert, Snackbar, Typography, IconButton, Tooltip, Drawer, useMediaQuery } from '@mui/material';
import AppLogo from './AppLogo';
import CertShieldHeader from './CertShieldHeader';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuIcon from '@mui/icons-material/Menu';
import MonitoredDomains from './MonitoredDomains';
import AlertsTable from './AlertsTable';
import Overview from './Overview';
import ScanControl from './ScanControl';
import ScanHistory from './ScanHistory';
import HelpPanel from './HelpPanel';
import ScanProgress from './ScanProgress';
import ScanLog from './ScanLog';
import { triggerScan, cancelScan, getScanHistory, getScanStatus, getSchedule, updateSchedule } from '../services/api';
import useVisiblePoll from '../hooks/useVisiblePoll';

const SIDEBAR_WIDTH = 300;
const parseTs = (iso) => iso ? new Date(iso + 'Z').getTime() : 0;


function Dashboard() {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedScanId, setSelectedScanId] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [showHelp, setShowHelp] = useState(false);
  const [helpSection, setHelpSection] = useState(null);
  const openHelp = useCallback((section = null) => { setHelpSection(section); setShowHelp(true); }, []);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:768px)');

  // Scan state
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(null);
  const [scanLogs, setScanLogs] = useState([]);
  const logCursorRef = useRef(0);
  const [history, setHistory] = useState([]);
  const prevStatusRef = useRef(null);
  const loadScanDataRef = useRef(null);
  const requestIdRef = useRef(0);
  const pollFailCountRef = useRef(0);

  // Schedule state
  const [schedEnabled, setSchedEnabled] = useState(true);
  const [schedInterval, setSchedInterval] = useState(4);
  const [nextAutoTs, setNextAutoTs] = useState(0);

  const handleRefresh = () => setRefreshTrigger(prev => prev + 1);
  const lastNotifRef = useRef({ key: '', at: 0 });
  const showNotification = useCallback((message, severity = 'success') => {
    // Dedupe identical back-to-back toasts (polling can re-trigger them)
    const key = `${severity}:${message}`;
    const now = Date.now();
    if (lastNotifRef.current.key === key && now - lastNotifRef.current.at < 4000) return;
    lastNotifRef.current = { key, at: now };
    setNotification({ open: true, message, severity });
  }, []);

  const inflightAbortRef = useRef(null);
  const clearScan = useCallback(() => setSelectedScanId(null), []);

  const loadScanData = async () => {
    const thisRequest = ++requestIdRef.current;
    // Cancel any prior in-flight pair so polls don't pile up on slow networks
    // or after logout/unmount.
    if (inflightAbortRef.current) {
      inflightAbortRef.current.abort();
    }
    const ac = new AbortController();
    inflightAbortRef.current = ac;
    try {
      const [historyRes, statusRes] = await Promise.all([
        getScanHistory({ signal: ac.signal, params: { limit: 15 } }),
        getScanStatus(logCursorRef.current, { signal: ac.signal }),
      ]);

      // Discard stale response if a newer request was fired
      if (thisRequest !== requestIdRef.current) return;

      pollFailCountRef.current = 0;
      setHistory(historyRes.data.slice(0, 15));
      setNextAutoTs(parseTs(statusRes.data.next_scan_at));
      const cur = statusRes.data.last_scan;
      // Only discovery scans drive the scanning state; NRD and feed runs are
      // short and have their own tiles (the server already filters, this is
      // a guard for older servers)
      const isDiscovery = cur?.trigger === 'manual' || cur?.trigger === 'scheduled';
      const running = isDiscovery && cur?.status === 'running';
      setScanning(running);
      setScanProgress(running ? (statusRes.data.progress || null) : null);

      // Accumulate new log lines
      const newLogs = running ? (statusRes.data.progress?.logs || []) : [];
      if (newLogs.length > 0) {
        setScanLogs(prev => {
          const merged = [...prev, ...newLogs];
          // Keep last 200 lines max
          return merged.length > 200 ? merged.slice(-200) : merged;
        });
        const maxIdx = newLogs[newLogs.length - 1].index;
        logCursorRef.current = maxIdx;
      }

      if (isDiscovery && prevStatusRef.current === 'running' && cur?.status === 'completed') {
        showNotification(`Scan complete: ${cur.domains_checked} domains checked, ${cur.new_detections} new alerts.`, 'success');
        handleRefresh();
      } else if (isDiscovery && prevStatusRef.current === 'running' && cur?.status === 'failed') {
        showNotification(`Scan failed: ${cur.error_message || 'Unknown error'}`, 'error');
      }
      prevStatusRef.current = cur?.status || null;
    } catch (err) {
      if (thisRequest !== requestIdRef.current) return;
      pollFailCountRef.current += 1;
      if (pollFailCountRef.current === 3) {
        showNotification('Unable to reach the server. Retrying...', 'warning');
      }
    }
  };

  const loadSchedule = async () => {
    try {
      const { data } = await getSchedule();
      setSchedEnabled(data.enabled);
      setSchedInterval(data.interval_hours);
      setNextAutoTs(parseTs(data.next_scan_at));
    } catch { /* silent */ }
  };

  loadScanDataRef.current = loadScanData;

  // useVisiblePoll runs immediately on mount, so only the schedule needs an explicit first load
  useEffect(() => {
    loadSchedule();
  }, []);

  useVisiblePoll(() => loadScanDataRef.current(), scanning ? 2500 : 5000, [scanning]);
  useEffect(() => {
    return () => {
      // Cancel any in-flight poll that started right before unmount/auth-change
      if (inflightAbortRef.current) {
        inflightAbortRef.current.abort();
        inflightAbortRef.current = null;
      }
    };
  }, [scanning]);

  const handleTriggerScan = async () => {
    // Invalidate any poll already in flight: a pre-trigger response saying
    // 'completed' must not flip the optimistic 'running' state back
    requestIdRef.current += 1;
    if (inflightAbortRef.current) {
      inflightAbortRef.current.abort();
      inflightAbortRef.current = null;
    }
    setScanning(true);
    try {
      const { data } = await triggerScan();
      if (data.status === 'running') {
        // The server answers 'already in progress' with the running scan's id:
        // keep its log and say so, instead of announcing a new scan
        const alreadyRunning = /already in progress/i.test(data.message || '');
        if (alreadyRunning) {
          showNotification('A scan is already running.', 'info');
        } else {
          setScanLogs([]);
          logCursorRef.current = 0;
          showNotification('Scan started: scanning all monitored domains.', 'info');
        }
        prevStatusRef.current = 'running';
        await loadScanData();
      } else if (data.status === 'completed') {
        await loadScanData();
        handleRefresh();
        showNotification(`Scan complete: ${data.new_detections || 0} new alerts.`, 'success');
        setScanning(false);
      }
    } catch (err) {
      // show the server's reason (e.g. the trigger cooldown) when it gives one
      const detail = err?.response?.data?.detail;
      showNotification(typeof detail === 'string' ? detail : 'Error starting scan.', 'error');
      setScanning(false);
    }
  };

  const handleCancelScan = async () => {
    try {
      await cancelScan();
      showNotification('Cancelling scan...', 'info');
    } catch {
      showNotification('Error cancelling scan.', 'error');
    }
  };

  const handleScheduleChange = async (changes) => {
    // Optimistic update
    if (changes.enabled !== undefined) setSchedEnabled(changes.enabled);
    if (changes.interval_hours !== undefined) setSchedInterval(changes.interval_hours);
    try {
      const { data } = await updateSchedule(changes);
      setSchedEnabled(data.enabled);
      setSchedInterval(data.interval_hours);
      setNextAutoTs(parseTs(data.next_scan_at));
    } catch (e) {
      showNotification(e.response?.data?.detail || 'Error saving schedule', 'error');
      loadSchedule();
    }
  };

  // 'Last Scan' means the last discovery scan, not a seconds-long NRD or feed run
  const lastCompletedScan = history.find(s => s.status === 'completed'
    && (s.trigger === 'manual' || s.trigger === 'scheduled'));
  const runningScan = history.find(s => s.status === 'running');

  // Typical discovery-scan duration (manual/scheduled only: NRD runs finish in
  // seconds and would wreck the average). Powers the sidebar ring + ETA.
  const avgScanSeconds = (() => {
    const durations = history
      .filter(s => s.status === 'completed'
        && (s.trigger === 'manual' || s.trigger === 'scheduled')
        && s.duration_seconds > 60)
      .slice(0, 5)
      .map(s => s.duration_seconds);
    if (!durations.length) return null;
    return durations.reduce((a, b) => a + b, 0) / durations.length;
  })();

  const sidebarContent = (
    <>
      {/* CertShield umbrella bar */}
      <CertShieldHeader currentTool="DomainProtection" />

      {/* Brand */}
      <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <AppLogo size={isMobile ? 42 : 62} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, color: '#e8e4f8', fontSize: isMobile ? '0.95rem' : '1.1rem', lineHeight: 1.2, letterSpacing: -0.2 }}>
              DomainProtection
            </Typography>
            <Typography sx={{ color: 'rgba(232,228,248,0.3)', fontSize: '0.68rem', mt: 0.3 }}>
              Brand Protection
            </Typography>
          </Box>
          <Tooltip title="Help & Information">
            <IconButton onClick={() => { openHelp(); if (isMobile) setMobileDrawerOpen(false); }} size="small" aria-label="Open help panel"
              sx={{ color: 'rgba(232,228,248,0.3)', '&:hover': { color: '#a78bfa', bgcolor: 'rgba(167,139,250,0.1)' } }}>
              <HelpOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Monitored Domains */}
      <Box sx={{ px: 2, pt: 2, pb: 1, flex: 1 }}>
        <MonitoredDomains onRefresh={handleRefresh} showNotification={showNotification}
          refreshTrigger={refreshTrigger} />
      </Box>

      {/* Scan Control + Schedule + Notifications pinned to bottom */}
      <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.07)', mt: 'auto' }}>
        <ScanControl
          scanning={scanning}
          scanProgress={scanProgress}
          runningScan={runningScan}
          avgScanSeconds={avgScanSeconds}
          onTriggerScan={() => { handleTriggerScan(); if (isMobile) setMobileDrawerOpen(false); }}
          onCancelScan={() => { handleCancelScan(); if (isMobile) setMobileDrawerOpen(false); }}
          schedEnabled={schedEnabled}
          schedInterval={schedInterval}
          onScheduleChange={handleScheduleChange}
          lastCompletedScan={lastCompletedScan}
          nextAutoTs={nextAutoTs}
          showNotification={showNotification}
          onClearHistory={(resetKey) => {
            // Reset log cursor + buffered logs so the next scan's logs stream
            // from the beginning instead of being stuck behind the old cursor.
            logCursorRef.current = 0;
            setScanLogs([]);
            prevStatusRef.current = null;
            setSelectedScanId(null);
            // A full/history reset deletes scan history: blank it immediately so
            // "Last Scan" and the time estimate clear now, not one poll later.
            if (resetKey === 'history' || resetKey === 'everything') setHistory([]);
            loadScanData();
            handleRefresh();
          }}
        />
      </Box>
    </>
  );

  // 100dvh where supported: on iOS Safari 100vh is the LARGE viewport
  // (address bar hidden), so a 100vh + overflow:hidden root leaves the
  // bottom strip of the inner scroller unreachable in portrait.
  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden',
               '@supports (height: 100dvh)': { height: '100dvh' } }}>
      {/* ── MOBILE TOP BAR ──────────────────────────────────── */}
      {isMobile && (
        <Box sx={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1100,
          bgcolor: '#0f0c1e', borderBottom: '1px solid rgba(255,255,255,0.07)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 1.5, py: 1, height: 'calc(48px + env(safe-area-inset-top))',
          pt: 'calc(8px + env(safe-area-inset-top))', // clear the notch when saved to homescreen
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => setMobileDrawerOpen(true)} size="small"
              sx={{ color: 'rgba(232,228,248,0.5)' }}>
              <MenuIcon />
            </IconButton>
            <AppLogo size={28} />
            <Typography sx={{ fontWeight: 700, color: '#e8e4f8', fontSize: '0.85rem' }}>
              DomainProtection
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            {scanning && (
              <Box sx={{
                width: 8, height: 8, borderRadius: '50%', bgcolor: '#fb923c',
                '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                animation: 'pulse 1.5s ease-in-out infinite',
              }} />
            )}
            <IconButton onClick={() => openHelp()} size="small"
              sx={{ color: 'rgba(232,228,248,0.3)' }}>
              <HelpOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* ── SIDEBAR (desktop) / DRAWER (mobile) ─────────────── */}
      {isMobile ? (
        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          PaperProps={{
            sx: {
              width: SIDEBAR_WIDTH, bgcolor: '#0f0c1e',
              display: 'flex', flexDirection: 'column',
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        <Box sx={{
          width: SIDEBAR_WIDTH, minWidth: SIDEBAR_WIDTH,
          borderRight: '1px solid rgba(255,255,255,0.07)',
          bgcolor: '#0f0c1e',
          display: 'flex', flexDirection: 'column',
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2 },
        }}>
          {sidebarContent}
        </Box>
      )}

      {/* ── MAIN CONTENT ────────────────────────────────────── */}
      <Box sx={{
        flex: 1, minWidth: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column',
        mt: isMobile ? 'calc(48px + env(safe-area-inset-top))' : 0,
        // iOS: momentum scrolling + no rubber-band chaining to the clipped
        // root; safe-area padding keeps the last row above the home indicator.
        WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain',
        // iOS Safari clips the tail of a flex-column that is also the scroller:
        // children get shrunk to fit and scrollHeight comes up short, so the
        // lower sections (identified domains, scan history) can't be reached.
        // Pin every section to its natural height so the full page scrolls.
        '& > *': { flexShrink: 0 },
        pb: 'env(safe-area-inset-bottom)',
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 3 },
      }}>
        {/* Scanning progress */}
        {scanning && (
          <ScanProgress
            progress={scanProgress}
            startedAt={runningScan?.scan_date}
            avgScanSeconds={avgScanSeconds}
          />
        )}

        {/* Scan Log (collapsible, visible during + after scan) */}
        {scanLogs.length > 0 && (
          <Box sx={{ px: isMobile ? 1.5 : 3, pb: 1.5, pt: scanning ? 0 : 2 }}>
            <ScanLog logs={scanLogs} scanning={scanning} />
          </Box>
        )}

        {/* Overview hero + live monitoring */}
        <Box sx={{ px: isMobile ? 1.5 : 3, pt: isMobile ? 1.5 : 3 }}>
          <Overview refreshTrigger={refreshTrigger} />
        </Box>

        {/* Alerts table with stats */}
        <Box sx={{ px: isMobile ? 1.5 : 3, pb: 1.5 }}>
          <AlertsTable
            refreshTrigger={refreshTrigger}
            showNotification={showNotification}
            selectedScanId={selectedScanId}
            onClearScan={clearScan}
            onOpenHelp={openHelp}
          />
        </Box>

        {/* Scan History (collapsible) */}
        <Box sx={{ px: isMobile ? 1.5 : 3, pb: 3 }}>
          <ScanHistory
            history={history}
            selectedScanId={selectedScanId}
            onScanSelect={setSelectedScanId}
            onOpenHelp={openHelp}
          />
        </Box>
      </Box>

      {/* Help panel + backdrop */}
      {showHelp && (
        <>
          <Box onClick={() => setShowHelp(false)}
            sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.5)', zIndex: 1200 }} />
          <HelpPanel onClose={() => setShowHelp(false)} initialSection={helpSection} />
        </>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(n => ({ ...n, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
      >
        <Alert
          onClose={() => setNotification(n => ({ ...n, open: false }))}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Dashboard;
